/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useEffect, useMemo, useReducer } from "react";
import { useSimpleEvent } from "../ui/hooks/useSimpleEvent";
import { contextMenuManager } from "./ContextMenuManager";
import { contextMenuRegister, TContextMenuId, TGetContextMenuParams } from "./contextMenuRegister";
import { BEM, getMouseOrTouchEventData, MouseOrTouchEvent } from "@adapptio/lib-components";
import { isFunction } from "@adapptio/lib-shared";

export interface IContextMenuProviderProps {
	className?: string;
}

interface IContextMenuProviderState<MenuId extends TContextMenuId = TContextMenuId> {
	id: MenuId;
	x: number;
	y: number;
	visible: boolean;
	triggerEvent: MouseOrTouchEvent;
	propsFromTrigger: TGetContextMenuParams<MenuId>;
	willLeave: boolean;
}

function reducer(
	state: IContextMenuProviderState,
	payload:
		| Partial<IContextMenuProviderState>
		| ((state: IContextMenuProviderState) => Partial<IContextMenuProviderState>)
) {
	return isFunction(payload) ? { ...state, ...payload(state) } : { ...state, ...payload };
}

export const ContextMenuProvider: React.FC<IContextMenuProviderProps> = ({ className }) => {
	const baseClass = BEM("edt-context-menu");

	if (className) {
		baseClass.add(className);
	}

	const [ state, setState ] = useReducer(reducer, {
		id: null,
		x: 0,
		y: 0,
		visible: false,
		triggerEvent: {} as MouseOrTouchEvent,
		propsFromTrigger: null,
		willLeave: false
	});

	const style = useMemo<React.CSSProperties>(
		() => ({
			top: state.y,
			left: state.x
		}),
		[ state.x, state.y ]
	);

	const hideMenu = useCallback(() => {
		setState((prevState) => ({
			visible: prevState.visible ? false : prevState.visible
		}));
	}, [ setState ]);

	useSimpleEvent(
		contextMenuManager.onMenuOpen,
		(params) => {
			if (params.id && contextMenuRegister[params.id]) {
				params.event.stopPropagation();

				const { clientX, clientY } = getMouseOrTouchEventData(params.event);

				requestAnimationFrame(() => {
					setState((prevState) => ({
						id: params.id,
						x: clientX,
						y: clientY,
						visible: true,
						triggerEvent: params.event,
						propsFromTrigger: params.params
					}));
				});
			}
		},
		[]
	);

	useSimpleEvent(contextMenuManager.onHideAll, hideMenu, [ hideMenu ]);

	useEffect(() => {
		if (state.visible) {
			window.addEventListener("resize", hideMenu);
			window.addEventListener("contextmenu", hideMenu);
			window.addEventListener("click", hideMenu);
			window.addEventListener("scroll", hideMenu);
			window.addEventListener("blur", hideMenu);
		}

		return () => {
			window.removeEventListener("resize", hideMenu);
			window.removeEventListener("contextmenu", hideMenu);
			window.removeEventListener("click", hideMenu);
			window.removeEventListener("scroll", hideMenu);
			window.removeEventListener("blur", hideMenu);
		};
	}, [ state.visible, hideMenu ]);

	if (!state.visible) {
		// @TODO: Animation
		return null;
	}

	const Component = state?.id && state?.id && contextMenuRegister[state.id];

	if (!Component) {
		return null;
	}

	return (
		<div className={baseClass.toClassName()} style={style}>
			<Component {...state.propsFromTrigger} />
		</div>
	);
};
